import axios from '../../helpers/bearerAxios';
import { logger } from '../loggingService';

const contractBaseUrl =
    import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL ||
    String(import.meta.env.VITE_ONEBUTTON_CONTRACT_API_URL || '').replace(/\/api\/Contract.*$/i, '') ||
    '';

const contractApiRoot = (): string => {
    const root = String(contractBaseUrl || '').replace(/\/$/, '');
    return root ? `${root}/api/Contract` : '';
};

/** Default Hover job UI base (Angular `environment.hoverJobUrl`). */
export const DEFAULT_HOVER_JOB_URL = 'https://hover.to/wr/jobs/';

/** Prod SF Hover Job lightning URL (Angular `environment.prod.ts`). */
const SF_HOVER_JOB_URL_PROD =
    'https://trinity-solar.lightning.force.com/lightning/r/HOVER_Job__c/';

/** Non-prod / sandbox SF Hover Job lightning URL (Angular develop/uat/pre-prod). */
const SF_HOVER_JOB_URL_SANDBOX =
    'https://trinity-solar--preprod.sandbox.lightning.force.com/lightning/r/HOVER_Job__c/';

export function getHoverJobUrl(): string {
    const fromEnv = String(import.meta.env.VITE_HOVER_JOB_URL || '').trim();
    return fromEnv || DEFAULT_HOVER_JOB_URL;
}

export function getSfHoverJobUrl(): string {
    const fromEnv = String(import.meta.env.VITE_SF_HOVER_JOB_URL || '').trim();
    if (fromEnv) return fromEnv.endsWith('/') ? fromEnv : `${fromEnv}/`;
    const envName = String(import.meta.env.VITE_ENV_NAME || '').toLowerCase();
    const isProd = envName === 'production' || envName === 'prod';
    return isProd ? SF_HOVER_JOB_URL_PROD : SF_HOVER_JOB_URL_SANDBOX;
}

export function getHoverMeasurementsPdfUrl(hoverJobId: string): string {
    const id = String(hoverJobId || '').replace(/^#/, '').trim();
    return `https://hover.to/api/v2/jobs/${encodeURIComponent(id)}/measurements.pdf`;
}

export function getHoverPropertyDetailsUrl(hoverJobId: string): string {
    const id = String(hoverJobId || '').replace(/^#/, '').trim();
    const base = getHoverJobUrl();
    return `${base.endsWith('/') ? base : `${base}/`}${encodeURIComponent(id)}`;
}

export function isHoverJobAvailable(hoverJobId: string | null | undefined): boolean {
    return String(hoverJobId ?? '').replace(/^#/, '').trim() !== '';
}

function isIosHoverClient(): boolean {
    const ua = navigator.userAgent;
    const isIpad = /Macintosh|iPad/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
    return /iPad|iPhone|iPod/.test(ua) || isIpad;
}

/**
 * Open the Hover property viewer for this job.
 * On iPad/iOS, tries Hover's native scheme first (Angular `openHoverUrl`), then falls back to web.
 */
export function openHoverJobViewer(hoverJobId: string | null | undefined): boolean {
    const id = String(hoverJobId ?? '').replace(/^#/, '').trim();
    if (!id) return false;
    const url = getHoverPropertyDetailsUrl(id);

    if (isIosHoverClient()) {
        const customSchemeUrl = `hihover://trinity/details/identifier/${id}`;
        const now = Date.now();
        window.setTimeout(() => {
            if (Date.now() - now < 3000 && !document.hidden) {
                window.location.href = url;
            }
        }, 2500);
        window.location.href = customSchemeUrl;
        return true;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
}

export type HoverJobMutationResponse = {
    IsSuccessful?: boolean;
    Data?: unknown;
    Message?: string;
    [key: string]: unknown;
};

export const hoverJobIdFromPayload = (payload: unknown): string | null => {
    const data = (payload as HoverJobMutationResponse | null | undefined)?.Data
        ?? (payload as { data?: unknown } | null | undefined)?.data
        ?? null;
    if (data == null || String(data).trim() === '') return null;
    return String(data).trim();
};

export type HoverRoofEstimateAdder = {
    AdderSelect: string;
    description: string;
    AdderQuantity: number;
    UnitPrice: number;
    AdderCost: number;
};

/** Client-side estimate row mapped from `HoverJobEstimateByopportunity` (Angular `RoofEstimate`). */
export type HoverRoofEstimate = {
    jobId: string;
    hoverEstimateID: string;
    status: 'Converted' | 'Not Converted';
    statusLabel: string;
    date1: string;
    date2: string;
    lowSq: number;
    normalSq: number;
    lowSqOverride: number;
    normalSqOverride: number;
    normalSlopeFloorRate: number;
    lowSlopeFloorRate: number;
    wastageSq: number;
    pricing: number;
    shingleType: string;
    shingleTypeName?: string | null;
    shingleManufacturer?: string | null;
    shingleProductCode?: string | null;
    shingleImage?: string | null;
    adders?: HoverRoofEstimateAdder[];
};

export type HoverJobEstimatesResponse = {
    HoverJobSFID?: string;
    HoverEstimateDTO?: any[];
    [key: string]: unknown;
};

/**
 * Trinity `AdminService.getHoverJobId` — GET `HoverJobID?opportunityID=`.
 * Response: `{ IsSuccessful?, Data: hoverJobId }`.
 */
const getHoverJobId = async (opportunityID: string | number): Promise<string | null> => {
    try {
        const url = `${contractApiRoot()}/HoverJobID?opportunityID=${encodeURIComponent(String(opportunityID))}`;
        const response = await axios.get(url);
        return hoverJobIdFromPayload(response?.data ?? response);
    } catch (error) {
        logger.error(error);
        return null;
    }
};

/**
 * Trinity `AdminService.createHoverJob` — GET `CreateHoverJob?opportunityID=&ownerID=`.
 * Response: `{ IsSuccessful?, Data: hoverJobId, Message? }`.
 */
const createHoverJob = async (
    opportunityID: string | number,
    ownerID: string | number | null | undefined,
): Promise<HoverJobMutationResponse> => {
    const url =
        `${contractApiRoot()}/CreateHoverJob` +
        `?opportunityID=${encodeURIComponent(String(opportunityID))}` +
        `&ownerID=${encodeURIComponent(String(ownerID ?? ''))}`;
    const response = await axios.post(url,{});
    return (response?.data ?? response) as HoverJobMutationResponse;
};

const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/**
 * After create, Angular polls `getHoverJobId` up to 3 times with 3s between attempts
 * when the create response has no job id.
 */
const pollHoverJobId = async (opportunityID: string | number, attempts = 3): Promise<string | null> => {
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
        const id = await getHoverJobId(opportunityID);
        if (id) return id;
        if (attempt < attempts) await delay(3000);
    }
    return null;
};

/**
 * Trinity `GenerateQuoteService.getHoverJobEstimates` — GET `HoverJobEstimateByopportunity?opportunityID=`.
 */
const getHoverJobEstimates = async (opportunityID: string | number): Promise<HoverJobEstimatesResponse | null> => {
    try {
        const url = `${contractApiRoot()}/HoverJobEstimateByopportunity?opportunityID=${encodeURIComponent(String(opportunityID))}`;
        const response = await axios.get(url);
        return (response?.data ?? response) as HoverJobEstimatesResponse;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `GenerateQuoteService.getShingleProducts` — GET `shingleProduct`.
 */
const getShingleProducts = async (): Promise<any[]> => {
    try {
        const url = `${contractApiRoot()}/shingleProduct`;
        const response = await axios.get(url);
        const res = response?.data ?? response;
        const data = Array.isArray(res) ? res : res?.Data || [];
        return Array.isArray(data) ? data : [];
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/** Map API DTO → UI estimate (preserve API typos `hoveJobID`, `watage`). */
export function mapHoverEstimateDto(item: any, resolveShingleImage?: (productCode: string | null) => string | null): HoverRoofEstimate {
    const productCode = item?.Shingle_product_code ? String(item.Shingle_product_code) : null;
    const resolvedImage =
        resolveShingleImage?.(productCode) ??
        (productCode ? `./assets/images/patterns/${productCode}.jpg` : null);

    return {
        jobId: item?.hoveJobID ? `#${item.hoveJobID}` : '#00000',
        hoverEstimateID: item?.hoverEstimateID || '',
        status: item?.Status === 'Converted' || item?.financepartnerQuoteId ? 'Converted' : 'Not Converted',
        statusLabel: item?.financepartnerQuoteId
            ? `Quote #${item.financepartnerQuoteId}`
            : 'No quote generated',
        date1: item?.CreateDate || '',
        date2: '',
        lowSq: Number(item?.LowSlope) || 0,
        normalSq: Number(item?.NormalSlope) || 0,
        lowSqOverride: Number(item?.Low_Slope_Override) || 0,
        normalSqOverride: Number(item?.Normal_Slope_Override) || 0,
        normalSlopeFloorRate: Number(item?.Normal_Slope_FloorRate) || 0,
        lowSlopeFloorRate: Number(item?.Low_Slope_FloorRate) || 0,
        wastageSq: Number(item?.watage) || 0,
        pricing: Number(item?.pricing) || 0,
        shingleType: item?.Shingle_color || '',
        shingleTypeName: item?.Shingle_type || '',
        shingleManufacturer: item?.Shingle_manufacturer || '',
        shingleProductCode: productCode,
        shingleImage: resolvedImage,
        adders: item?.Adders
            ? item.Adders.map((adder: any) => {
                  const qty = Number(adder.Qty) || 0;
                  const totalCost = Number(adder.TotalCost) || 0;
                  const unitFromApi = Number(adder.UnitPrice) || 0;
                  /* Some Hover adder rows (e.g. Skylights Fixed) omit UnitPrice but include TotalCost. */
                  const unitPrice =
                      unitFromApi > 0 ? unitFromApi : qty > 0 && totalCost > 0 ? totalCost / qty : totalCost;
                  return {
                      AdderSelect: adder.AdderName,
                      description: '',
                      AdderQuantity: qty,
                      UnitPrice: unitPrice,
                      AdderCost: totalCost,
                  };
              })
            : [],
    };
}

const hoverServices = {
    getHoverJobId,
    createHoverJob,
    pollHoverJobId,
    getHoverJobEstimates,
    getShingleProducts,
};

export default hoverServices;
